import type {
    HeaderValues,
    HttpCookie,
    HttpHeaders,
    MutableHttpHeaders,
    ReadonlyHeaderValues,
    ResponseCookie
} from '../../types/web/http';

import { Cookie } from 'tough-cookie';

function parseHost(headers: HttpHeaders, defaultHost?: string): string | undefined {
    let host = headers.get('x-forwarded-for');
    if (host === undefined) {
        host = headers.get('x-forwarded-host');
        if (Array.isArray(host)) {
            host = host[0];
        }
        if (host) {
            const port = headers.one('x-forwarded-port');
            if (port) {
                host = `${host}:${port}`;
            }
        }
        host ??= headers.one('host');
    }
    if (Array.isArray(host)) {
        host = host[0];
    }
    if (host) {
        return (host as string).split(',', 1)[0].trim();
    }



    return defaultHost;
}

function parseProtocol(headers: HttpHeaders, defaultProtocol: string): string {
    let proto = headers.get('x-forwarded-proto');

    if (Array.isArray(proto)) {
        proto = proto[0];
    }
    if (proto !== undefined) {
        return (proto as string).split(',', 1)[0].trim();
    }
    return defaultProtocol;
}

export abstract class AbstractHttpMessage<Headers extends HttpHeaders = HttpHeaders> {
    readonly #headers: Headers;
    protected constructor(headers: Headers) {
        this.#headers = headers;
    }
    get headers(): Headers {
        return this.#headers;
    }
}

export abstract class AbstractHttpRequest<Headers extends HttpHeaders = HttpHeaders> extends AbstractHttpMessage<Headers> {
    private static logIdCounter = 0;
    #id?: string

    get id(): string {
        if (this.#id === undefined) {
            this.#id = `${this.initId()}-${++AbstractHttpRequest.logIdCounter}`;

        }
        return this.#id;
    }

    protected initId(): string {
        return 'request';
    }

    get cookies(): HttpCookie[] {
        return parseCookies(this.headers);
    }

    protected parseHost(defaultHost?: string): string | undefined {
        return parseHost(this.headers, defaultHost);
    }


    protected parseProtocol(defaultProtocol: string): string {
        return parseProtocol(this.headers, defaultProtocol);
    }
}

export abstract class AbstractHttpResponse<Headers extends HttpHeaders = HttpHeaders> extends AbstractHttpMessage<Headers> {
    get cookies(): ResponseCookie[] {
        return parseResponseCookies(this.headers);
    }

    protected setCookieValue(responseCookie: ResponseCookie): string {
        const cookie = new Cookie({
            key: responseCookie.name,
            value: responseCookie.value,
            maxAge: responseCookie.maxAge,
            domain: responseCookie.domain,
            path: responseCookie.path,
            secure: responseCookie.secure,
            httpOnly: responseCookie.httpOnly,
            sameSite: responseCookie.sameSite
        });
        return cookie.toString();
    }

}

function parseHeader(value: string): string[] {
    const list: string[] = [];
    {
        let start = 0;
        let end = 0;

        for (let i = 0; i < value.length; i++) {
            switch (value.charCodeAt(i)) {
                case 0x20: // space
                    if (start === end) {
                        start = end = i + 1;
                    }
                    break;
                case 0x2c: // comma
                    list.push(value.slice(start, end));
                    start = end = i + 1;
                    break;
                default:
                    end = end + 1;
                    break;
            }
        }
        list.push(value.slice(start, end));
    }

    return list;
}

function toList(values: number | string | (readonly string[]) | undefined): string[] {
    if (typeof values === 'string') {
        values = [values];
    }
    if (typeof values === 'number') {
        values = [String(values)];
    }
    const list: string[] = [];
    if (values) {
        for (const value of values) {
            if (value) {
                list.push(...parseHeader(value));
            }
        }
    }
    return list;
}

function parseCookies(headers: HttpHeaders): HttpCookie[] {
    return headers.list('cookie')
        .map(s => s.split(';').map((cs) => Cookie.parse(cs)))
        .flat(1)
        .filter((tc) => tc !== undefined)
        .map((tc) => {
            const result: HttpCookie = Object.freeze({name: tc.key, value: tc.value});
            return result;
        });
}

type Mutable<T> = {-readonly [K in keyof T]: T[K]};

function parseResponseCookies(headers: HttpHeaders): ResponseCookie[] {
    return headers.list('set-cookie').map((cookie) => {
        const parsed = Cookie.parse(cookie);
        if (parsed) {
            const result: Mutable<ResponseCookie> = {name: parsed.key, value: parsed.value, maxAge: Number(parsed.maxAge ?? -1)};
            if (parsed.httpOnly) result.httpOnly = true;
            if (parsed.domain) result.domain = parsed.domain;
            if (parsed.path) result.path = parsed.path;
            if (parsed.secure) result.secure = true;
            if (parsed.httpOnly) result.httpOnly = true;
            if (parsed.sameSite) result.sameSite = parsed.sameSite as 'strict' | 'lax' | 'none';
            return Object.freeze(result);
        }
    }).filter((cookie): cookie is ResponseCookie => cookie !== undefined);
}


export abstract class AbstractHttpHeaders<Values extends HeaderValues | ReadonlyHeaderValues> {
    protected constructor() {}
    abstract get(name: string): Values

    toList(name: string): string[] {
        const values = this.get(name);
        return toList(values);
    }
}

export class MapHttpHeaders extends Map<string, (readonly string[])> implements MutableHttpHeaders {

    get(name: string) {
        return super.get(name.toLowerCase());
    }

    one(name: string): string | undefined {
        return this.get(name)?.[0];
    }

    list(name: string) {
        const values = super.get(name.toLowerCase());
        return toList(values);
    }

    set(name: string, value: number | string | (readonly string[]) | undefined): this {
        if (typeof value === 'number') {
            value = String(value);
        }
        if (typeof value === 'string') {
            value = [value];
        }
        if (value) {
            return super.set(name.toLowerCase(), value);
        }
        else {
            super.delete(name.toLowerCase());
            return this;
        }
    }

    add(name: string, value: string | (readonly string[])) {
        const prev = super.get(name.toLowerCase());
        if (typeof value === 'string') {
            value = [value];
        }
        if (prev) {
            value = prev.concat(value);
        }
        this.set(name, value);
        return this;
    }
}
